import { NewNotificationIcon } from '@/Components/Common/Icons';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import '../../Dashboard.less';
import { DashboardSSEEventContext } from '../../DashboardContext';
import { ALERT_CARDS_DAYS, useAlertEventsStream } from '../../hooks';
import { useRegisterReportSection } from '../../reportUtils';
import AlertsCard from './AlertsCard';
import {
  getTopApiAlerts,
  isSupportedDashboardAlertType,
  mergeAlertCards,
  normalizeSseAlertForCard,
} from './alertCardsUtils';

const Alerts = ({
  timezone,
  is24HourFormat,
  useruuid,
  locale,
  hc200DeviceUuid,
  isAlertsApplicable,
  redirectToAlertsTab,
  supportedCategories,
}) => {
  const trans = useSelector(translator);

  // The streamed list is kept sorted newest first, so the cards update as each batch arrives.
  const { data: alerts } = useAlertEventsStream({
    useruuid,
    hc200DeviceUuid,
    supportedCategories,
    days: ALERT_CARDS_DAYS,
  });

  // Real-time alerts received while the dashboard is open, newest first.
  const [sseAlerts, setSseAlerts] = useState([]);

  useEffect(() => setSseAlerts([]), [useruuid]);

  const { latestEvent: sseEvent } = useContext(DashboardSSEEventContext);

  useEffect(() => {
    const normalizedSseAlert = normalizeSseAlertForCard(sseEvent);

    if (
      isEmpty(normalizedSseAlert) ||
      !isSupportedDashboardAlertType(get(normalizedSseAlert, 'message_type', ''))
    ) {
      return;
    }

    setSseAlerts((previousAlerts) => mergeAlertCards([normalizedSseAlert], previousAlerts));
  }, [sseEvent]);

  const cardAlerts = useMemo(
    () => mergeAlertCards(sseAlerts, getTopApiAlerts(alerts)),
    [sseAlerts, alerts]
  );

  // Register the alerts actually shown on the dashboard so the PDF report never
  // loses information (e.g. real-time SSE alerts absent from the API history).
  useRegisterReportSection('alerts', cardAlerts, [cardAlerts]);

  const shouldShowSkeleton = Boolean(isAlertsApplicable && isEmpty(cardAlerts));
  if (isEmpty(cardAlerts) && !isAlertsApplicable) return null;

  return (
    <div className="alert-section">
      <div className="section-header">
        <NewNotificationIcon className="section-icon" />
        <h3 className="section-title">{trans('ALERTS')}</h3>
      </div>
      <AlertsCard
        alerts={cardAlerts}
        is24hours={is24HourFormat}
        timezone={timezone}
        locale={locale}
        isFetchingAlerts={shouldShowSkeleton}
      />
      {cardAlerts.length > 0 && (
        <div className="more-alerts">
          <button className="show-more-alerts" onClick={redirectToAlertsTab}>
            {trans('SHOW_MORE_ALERTS')}
          </button>
        </div>
      )}
    </div>
  );
};

Alerts.propTypes = {
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  useruuid: PropTypes.string,
  locale: PropTypes.string,
  hc200DeviceUuid: PropTypes.string,
  isAlertsApplicable: PropTypes.bool,
  redirectToAlertsTab: PropTypes.func,
  supportedCategories: PropTypes.array,
};

export default Alerts;
